import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Inbox, CalendarCheck, Settings, Home as HomeIcon } from "lucide-react";
import { TOKEN_KEY, authMe } from "@/lib/api";
import Logo from "@/components/site/Logo";
import { toast } from "sonner";

const NAV = [
    { to: "/admin", icon: LayoutDashboard, label: "Analytics", end: true },
    { to: "/admin/leads", icon: Inbox, label: "Leads" },
    { to: "/admin/consultations", icon: CalendarCheck, label: "Consultations" },
    { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout() {
    const [checking, setChecking] = useState(true);
    const [authed, setAuthed] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setChecking(false);
            setAuthed(false);
            return;
        }
        authMe()
            .then(() => {
                setAuthed(true);
                setChecking(false);
            })
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setAuthed(false);
                setChecking(false);
            });
    }, []);

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-bg">
                <div className="text-brand-muted text-sm">Checking session…</div>
            </div>
        );
    }
    if (!authed) return <Navigate to="/admin/login" replace />;

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        toast.success("Signed out.");
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-brand-bg grid grid-cols-1 md:grid-cols-[260px_1fr]">
            <aside
                data-testid="admin-sidebar"
                className="border-r border-brand-border bg-brand-surface flex flex-col"
            >
                <div className="p-6 border-b border-brand-border">
                    <Logo />
                    <div className="mt-3 text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
                        Admin console
                    </div>
                </div>
                <nav className="p-4 flex-1 space-y-1">
                    {NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors ${
                                        isActive
                                            ? "bg-brand-primary text-brand-bg"
                                            : "text-brand-muted hover:bg-brand-surfaceAlt hover:text-brand-primary"
                                    }`
                                }
                            >
                                <Icon size={16} />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-4 border-t border-brand-border space-y-1">
                    <NavLink
                        to="/"
                        data-testid="admin-view-site"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-brand-muted hover:text-brand-primary"
                    >
                        <HomeIcon size={16} /> View site
                    </NavLink>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-muted hover:text-brand-primary"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </aside>
            <main className="p-6 md:p-10">
                <Outlet />
            </main>
        </div>
    );
}
